import React, { useCallback, useRef, useState } from 'react';
import Text from '@amzn/meridian/text';
import Input from '@amzn/meridian/input';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Button from '@amzn/meridian/button';
import Popover from '@amzn/meridian/popover';

/**
 * Inline, free-text editable label used for the per-slot labels above employee icons.
 *
 * - When `editable` is false it renders as plain text (unchanged legacy behavior).
 * - When `editable` is true, clicking the text opens a small Popover with an input + Save/Cancel.
 *   The Popover is used (rather than an inline input) because each slot is only ~60px wide, so an
 *   inline editor overflows and gets clipped at the board's left/right edges. A Popover renders in
 *   an overlay and auto-repositions to stay on screen.
 *   Saving calls `onSave(trimmedText)`. An empty value is passed through so callers can treat it as
 *   "clear the override" (revert the slot to its default label).
 *
 * The label value is controlled by the parent; local state only holds the in-progress draft.
 */
export const EditableLabel = ({ value, editable = false, onSave, placeholder = 'Label', textType = 'B100' }) => {
  const anchorRef = useRef();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || '');

  const beginEdit = useCallback(
    (event) => {
      // Don't let the click bubble to the employee icon (which opens its own popover).
      if (event && event.stopPropagation) {
        event.stopPropagation();
      }
      setDraft(value || '');
      setOpen(true);
    },
    [value]
  );

  const close = useCallback(() => {
    setOpen(false);
    setDraft(value || '');
  }, [value]);

  const save = useCallback(() => {
    setOpen(false);
    if (onSave) {
      onSave((draft || '').trim());
    }
  }, [draft, onSave]);

  if (!editable) {
    return (
      <Text transform={'capitalize'} type={textType} truncate={'true'}>
        {value ? value : ''}
      </Text>
    );
  }

  return (
    <React.Fragment>
      <span
        ref={anchorRef}
        onClick={beginEdit}
        onKeyDown={(e) => {
          // Keyboard access: Enter/Space open the editor, matching the click behavior.
          // preventDefault on Space stops the page from scrolling.
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            beginEdit(e);
          }
        }}
        role="button"
        tabIndex={0}
        title="Click to edit label"
        style={{
          cursor: 'pointer',
          borderBottom: '1px dashed #6E777F',
          lineHeight: 1,
          // Constrain to the ~60px slot so long default labels (e.g. process names like
          // "Auto Divert Straightener") clip with an ellipsis instead of overflowing and
          // overlapping neighboring icons. Full text is still editable via the popover.
          display: 'inline-block',
          maxWidth: '56px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          verticalAlign: 'bottom'
        }}>
        <Text transform={'capitalize'} type={textType} truncate={'true'}>
          {value ? value : placeholder}
        </Text>
      </span>
      <Popover anchorNode={anchorRef.current} open={open} onClose={close} position="top">
        <Column spacing="200" spacingInset="200">
          <Input
            size="small"
            type="text"
            value={draft}
            onChange={setDraft}
            onSubmit={save}
            placeholder={placeholder}
            width={180}
          />
          <Row spacing="200" alignmentHorizontal="end">
            <Button size="small" type="tertiary" onClick={close}>
              Cancel
            </Button>
            <Button size="small" type="primary" onClick={save}>
              Save
            </Button>
          </Row>
        </Column>
      </Popover>
    </React.Fragment>
  );
};
